// AGC: Admin Panel - Task Templates Configuration
// AGC: Manage task templates and phenological stages for each crop
// AGC: CRUD operations for farm operation templates used in lot planning

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../../hooks'
import { MetricCard } from './MetricCard'
import { SectionCard } from './SectionCard'
// AGC: [AR][MANEJO] AR tab — renders the AR manejo templates editor; Brazil's UI below is unchanged.
import { CountrySwitcher, type ModelCountry } from './CountrySwitcher'
import { ArManejoTemplatesConfig } from './ArManejoTemplatesConfig'
import { getBackendUrl } from '../../../utils/backendUrl'
import { type TaskTemplate, type PhenologicalStage, type TaskCategory, type TaskInput } from '../../../types/admin/models'
import { type InputMaster, INPUT_CATEGORIES, getInputCategoryInfo } from '../../../types/inventory'
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  RefreshCw,
  Download,
  Upload,
  Filter,
  ListTodo,
  Sprout,
  ChevronDown,
  ChevronRight,
  GripVertical,
  AlertTriangle,
  Check,
  Package,
} from 'lucide-react'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface CropTaskTemplates {
  cropId: string
  cropName: string
  cropIcon: string
  tasks: TaskTemplate[]
  stages: PhenologicalStage[]
  updatedAt?: string
}

const TASK_CATEGORIES: { id: TaskCategory; label: string; icon: string }[] = [
  { id: 'preparation', label: 'Preparo', icon: '🔧' },
  { id: 'planting', label: 'Plantio', icon: '🌱' },
  { id: 'spraying', label: 'Pulverização', icon: '💧' },
  { id: 'fertilizing', label: 'Adubação', icon: '🧪' },
  { id: 'harvest', label: 'Colheita', icon: '🚜' },
  { id: 'monitoring', label: 'Monitoramento', icon: '👁️' },
  { id: 'other', label: 'Outro', icon: '📋' },
]

// =============================================================================
// AGC: COMPONENT
// =============================================================================

export const TaskTemplatesConfig: React.FC = () => {
  // AGC: [AR][MANEJO] Country tab — 'AR' renders ArManejoTemplatesConfig; 'BR' keeps the existing UI.
  const [country, setCountry] = useState<ModelCountry>('BR')
  const { t } = useTranslation()

  // AGC: State
  const [templates, setTemplates] = useState<Record<string, CropTaskTemplates>>({})
  const [inputs, setInputs] = useState<InputMaster[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [selectedCrop, setSelectedCrop] = useState<string | null>(null)
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set())
  const [configSource, setConfigSource] = useState<'defaults' | 'firestore'>('defaults')
  
  // AGC: Modal state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<TaskTemplate | null>(null)
  const [taskFormData, setTaskFormData] = useState<Partial<TaskTemplate>>({})

  // AGC: Load templates from API
  const loadTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const backendUrl = getBackendUrl()
      
      // AGC: Load task templates
      const templatesResponse = await fetch(`${backendUrl}/api/v1/admin/config/task-templates`, {
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
      })
      
      if (!templatesResponse.ok) {
        throw new Error(`Failed to load templates: ${templatesResponse.statusText}`)
      }
      
      const templatesData = await templatesResponse.json()
      
      if (templatesData.success) {
        setTemplates(templatesData.data.templates || {})
        setConfigSource(templatesData.data.source || 'defaults')
        
        // AGC: Select first crop if none selected
        const crops = Object.keys(templatesData.data.templates || {})
        if (crops.length > 0 && !selectedCrop) {
          setSelectedCrop(crops[0])
        }
      }
      
      // AGC: Load inputs for task input selection
      const inputsResponse = await fetch(`${backendUrl}/api/v1/admin/config/inputs?activeOnly=true`, {
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
      })
      
      if (inputsResponse.ok) {
        const inputsData = await inputsResponse.json()
        if (inputsData.success) {
          setInputs(inputsData.data.inputs || [])
        }
      }
      
      console.log(`✅ AGC: [TASK_TEMPLATES_ADMIN] Loaded templates`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.error('❌ AGC: [TASK_TEMPLATES_ADMIN] Error:', message)
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [selectedCrop])

  // AGC: Load on mount
  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  // AGC: Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // AGC: Get current crop's tasks and stages
  const currentCrop = selectedCrop ? templates[selectedCrop] : null
  const tasks = currentCrop?.tasks || []
  const stages = currentCrop?.stages || []
  const crops = Object.keys(templates)

  // AGC: Calculate stats
  const stats = {
    crops: crops.length,
    totalTasks: Object.values(templates).reduce((acc, crop) => acc + (crop.tasks?.length || 0), 0),
    currentTasks: tasks.length,
    stages: stages.length,
  }

  // AGC: Toggle task expansion
  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev)
      if (next.has(taskId)) {
        next.delete(taskId)
      } else {
        next.add(taskId)
      }
      return next
    })
  }

  // AGC: Open task modal
  const handleOpenTaskModal = (task?: TaskTemplate) => {
    if (task) {
      setEditingTask(task)
      setTaskFormData(task)
    } else {
      setEditingTask(null)
      setTaskFormData({
        cropId: selectedCrop || '',
        title: '',
        category: 'spraying',
        icon: '💧',
        daysFromPlanting: 0,
        inputs: [],
        order: tasks.length + 1,
        isRequired: false,
        isActive: true,
      })
    }
    setIsTaskModalOpen(true)
  }

  // AGC: Save task
  const handleSaveTask = async () => {
    if (!taskFormData.title || !taskFormData.cropId) {
      setError('Please fill in all required fields')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const url = editingTask 
        ? `${backendUrl}/api/v1/admin/config/task-templates/${editingTask.id}`
        : `${backendUrl}/api/v1/admin/config/task-templates`
      
      const response = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...taskFormData,
          cropName: currentCrop?.cropName,
          cropIcon: currentCrop?.cropIcon,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save task')
      }

      setSuccessMessage(editingTask ? 'Task updated successfully' : 'Task created successfully')
      setIsTaskModalOpen(false)
      setEditingTask(null)
      setTaskFormData({})
      loadTemplates()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  // AGC: Delete task
  const handleDeleteTask = async (task: TaskTemplate) => {
    if (!confirm(`Are you sure you want to deactivate "${task.title}"?`)) {
      return
    }

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/task-templates/${task.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Bearer admin-token',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete task')
      }

      setSuccessMessage('Task deactivated successfully')
      loadTemplates()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    }
  }

  // AGC: Seed defaults
  const handleSeedDefaults = async () => {
    if (!confirm('This will add default task templates to the database. Continue?')) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/task-templates/seed`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer admin-token',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to seed templates')
      }

      setSuccessMessage(data.message || 'Templates seeded successfully')
      loadTemplates()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  // AGC: Add input to task form
  const handleAddInput = () => {
    const newInput: TaskInput = {
      inputId: '',
      inputName: '',
      category: 'herbicide',
      quantityPerHa: 1,
      unit: 'L',
    }
    setTaskFormData(prev => ({
      ...prev,
      inputs: [...(prev.inputs || []), newInput],
    }))
  }

  // AGC: Update input in task form
  const handleUpdateInput = (index: number, field: keyof TaskInput, value: string | number) => {
    setTaskFormData(prev => {
      const inputs = [...(prev.inputs || [])]
      inputs[index] = { ...inputs[index], [field]: value }
      
      // AGC: Auto-fill name and category when inputId is selected
      if (field === 'inputId') {
        const selectedInput = inputs.find(i => i.id === value) as InputMaster | undefined
        const inputFromCatalog = inputs.find(i => i.id === value)
        if (!inputFromCatalog) {
          const catalogInput = inputs.find(i => i.id === value)
          if (catalogInput) {
            inputs[index].inputName = (catalogInput as unknown as InputMaster).name
            inputs[index].category = (catalogInput as unknown as InputMaster).category
            inputs[index].unit = (catalogInput as unknown as InputMaster).unit
            inputs[index].quantityPerHa = (catalogInput as unknown as InputMaster).defaultQuantityPerHa || 1
          }
        }
      }
      
      return { ...prev, inputs }
    })
  }

  // AGC: Remove input from task form
  const handleRemoveInput = (index: number) => {
    setTaskFormData(prev => ({
      ...prev,
      inputs: (prev.inputs || []).filter((_, i) => i !== index),
    }))
  }

  // AGC: Get category info
  const getCategoryInfo = (category: TaskCategory) => {
    return TASK_CATEGORIES.find(c => c.id === category)
  }

  // AGC: [AR][MANEJO] Argentina tab renders its own manejo editor; Brazil keeps the UI below.
  if (country === 'AR') {
    return (
      <div className="space-y-6">
        <CountrySwitcher value={country} onChange={setCountry} />
        <ArManejoTemplatesConfig />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <CountrySwitcher value={country} onChange={setCountry} />
      {/* AGC: Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ListTodo className="w-7 h-7 text-blue-600" />
            Task Templates
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage task templates and phenological stages for each crop
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full ${
            configSource === 'firestore' 
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' 
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
          }`}>
            {configSource === 'firestore' ? '🔥 Firestore' : '⚠️ Defaults'}
          </span>
        </div>
      </div>

      {/* AGC: Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300">{successMessage}</span>
        </div>
      )}

      {/* AGC: Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={<Sprout className="w-5 h-5" />}
          value={stats.crops}
          label="Crops"
          color="#22C55E"
        />
        <MetricCard
          icon={<ListTodo className="w-5 h-5" />}
          value={stats.totalTasks}
          label="Total Tasks"
          color="#3B82F6"
        />
        <MetricCard
          icon={<ListTodo className="w-5 h-5" />}
          value={stats.currentTasks}
          label={`${selectedCrop || 'Selected'} Tasks`}
          color="#8B5CF6"
        />
        <MetricCard
          icon={<Sprout className="w-5 h-5" />}
          value={stats.stages}
          label="Stages"
          color="#F59E0B"
        />
      </div>

      {/* AGC: Crop Selector and Actions */}
      <div className="flex flex-wrap gap-4 items-center">
        <select
          value={selectedCrop || ''}
          onChange={(e) => setSelectedCrop(e.target.value)}
          className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900 min-w-[200px]"
        >
          <option value="">Select Crop...</option>
          {crops.map(cropId => {
            const crop = templates[cropId]
            return (
              <option key={cropId} value={cropId}>
                {crop?.cropIcon} {crop?.cropName || cropId}
              </option>
            )
          })}
        </select>

        <button
          onClick={() => handleOpenTaskModal()}
          disabled={!selectedCrop}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:opacity-90 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add Task
        </button>
        
        <button
          onClick={loadTemplates}
          disabled={loading}
          className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        
        <button
          onClick={handleSeedDefaults}
          disabled={saving}
          className="px-4 py-2 border border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          Seed Defaults
        </button>
      </div>

      {/* AGC: Task List */}
      <SectionCard 
        title={`${currentCrop?.cropIcon || '🌾'} ${currentCrop?.cropName || 'Tasks'}`} 
        icon={<ListTodo className="w-5 h-5" />}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{selectedCrop ? 'No tasks for this crop' : 'Select a crop to view tasks'}</p>
            {selectedCrop && (
              <button
                onClick={handleSeedDefaults}
                className="mt-4 text-blue-600 hover:underline"
              >
                Seed default task templates
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {tasks
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((task) => {
                const isExpanded = expandedTasks.has(task.id)
                const catInfo = getCategoryInfo(task.category)
                
                return (
                  <div 
                    key={task.id}
                    className={`border border-slate-200 dark:border-white/5 rounded-lg ${
                      task.isActive === false ? 'opacity-50' : ''
                    }`}
                  >
                    {/* AGC: Task Header */}
                    <div 
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                      onClick={() => toggleTaskExpansion(task.id)}
                    >
                      <GripVertical className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <span className="text-xl">{task.icon || catInfo?.icon}</span>
                      <div className="flex-1">
                        <div className="font-medium text-slate-900 dark:text-slate-100">{task.title}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          Day {task.daysFromPlanting} • {task.inputs?.length || 0} inputs
                          {task.phenologicalStageCode && ` • ${task.phenologicalStageCode}`}
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400`}>
                        {catInfo?.label}
                      </span>
                      {task.isRequired && (
                        <span className="px-2 py-1 text-xs rounded-full bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400">Required</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOpenTaskModal(task); }}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-white/10 rounded"
                      >
                        <Edit2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteTask(task); }}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </button>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      )}
                    </div>
                    
                    {/* AGC: Task Details (expanded) */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-100 dark:border-white/5">
                        {task.description && (
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-3 mb-3">
                            {task.description}
                          </p>
                        )}
                        
                        {/* AGC: Inputs */}
                        {task.inputs && task.inputs.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                              <Package className="w-4 h-4" />
                              Required Inputs
                            </h5>
                            <div className="grid gap-2">
                              {task.inputs.map((input, idx) => {
                                const inputCatInfo = getInputCategoryInfo(input.category as any)
                                return (
                                  <div 
                                    key={idx}
                                    className="flex items-center gap-3 bg-slate-50 dark:bg-white/[0.02] rounded p-2 text-sm"
                                  >
                                    <span>{inputCatInfo?.icon || '📦'}</span>
                                    <span className="font-medium text-slate-900 dark:text-slate-100">
                                      {input.inputName}
                                    </span>
                                    <span className="text-slate-500 dark:text-slate-400">
                                      {input.quantityPerHa} {input.unit}/ha
                                    </span>
                                    {input.notes && (
                                      <span className="text-xs text-slate-400 dark:text-slate-500">({input.notes})</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        )}
      </SectionCard>

      {/* AGC: Task Edit Modal */}
      {isTaskModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {editingTask ? 'Edit Task' : 'Add New Task'}
              </h3>
              <button onClick={() => setIsTaskModalOpen(false)}>
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* AGC: Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Task Title *
                </label>
                <input
                  type="text"
                  value={taskFormData.title || ''}
                  onChange={(e) => setTaskFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  placeholder="e.g., Aplicação de Herbicida"
                />
              </div>

              {/* AGC: Category & Icon */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={taskFormData.category || 'spraying'}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, category: e.target.value as TaskCategory }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    {TASK_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Icon
                  </label>
                  <input
                    type="text"
                    value={taskFormData.icon || ''}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                    placeholder="💧"
                  />
                </div>
              </div>

              {/* AGC: Days & Stage */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Days from Planting
                  </label>
                  <input
                    type="number"
                    value={taskFormData.daysFromPlanting || 0}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, daysFromPlanting: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Use negative for pre-planting tasks</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Phenological Stage
                  </label>
                  <select
                    value={taskFormData.phenologicalStageCode || ''}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, phenologicalStageCode: e.target.value || undefined }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  >
                    <option value="">Select stage...</option>
                    {stages.map(stage => (
                      <option key={stage.id} value={stage.code}>{stage.code} - {stage.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* AGC: Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={taskFormData.description || ''}
                  onChange={(e) => setTaskFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                  placeholder="Detailed task description..."
                />
              </div>

              {/* AGC: Required Inputs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Required Inputs
                  </label>
                  <button
                    type="button"
                    onClick={handleAddInput}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Input
                  </button>
                </div>
                
                {(taskFormData.inputs || []).map((input, index) => (
                  <div key={index} className="flex gap-2 mb-2 items-start">
                    <select
                      value={input.inputId}
                      onChange={(e) => {
                        const selectedInput = inputs.find(i => i.id === e.target.value)
                        if (selectedInput) {
                          handleUpdateInput(index, 'inputId', e.target.value)
                          handleUpdateInput(index, 'inputName', selectedInput.name)
                          handleUpdateInput(index, 'category', selectedInput.category)
                          handleUpdateInput(index, 'unit', selectedInput.unit)
                          handleUpdateInput(index, 'quantityPerHa', selectedInput.defaultQuantityPerHa)
                        }
                      }}
                      className="flex-1 px-2 py-1 border border-slate-200 dark:border-white/10 rounded bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-sm"
                    >
                      <option value="">Select input...</option>
                      {inputs.map(i => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={input.quantityPerHa}
                      onChange={(e) => handleUpdateInput(index, 'quantityPerHa', parseFloat(e.target.value) || 0)}
                      className="w-20 px-2 py-1 border border-slate-200 dark:border-white/10 rounded bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-sm"
                      placeholder="Qty"
                    />
                    <span className="px-2 py-1 text-sm text-slate-500 dark:text-slate-400">{input.unit || 'unit'}/ha</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveInput(index)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                    >
                      <X className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>

              {/* AGC: Flags */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={taskFormData.isRequired !== false}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Required task</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={taskFormData.isActive !== false}
                    onChange={(e) => setTaskFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Active</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-white/5 flex justify-end gap-3">
              <button
                onClick={() => setIsTaskModalOpen(false)}
                className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTask}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:opacity-90 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {editingTask ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TaskTemplatesConfig

